import { useEffect, useRef, useCallback, useState } from 'react';
import { isEqual, cloneDeep } from 'lodash';

const AUTOSAVE_DEBOUNCE_MS = 2000; // 2 seconds

export const AutosaveStatus = {
  SAVED: Symbol('saved'),
  SAVING: Symbol('saving'),
  UNSAVED: Symbol('unsaved'),
};

// Helper function to check if campaign audience has topics (required)
const hasCampaignAudienceTopics = audience => {
  if (!audience) return false;
  return (
    audience.topicIds &&
    Array.isArray(audience.topicIds) &&
    audience.topicIds.length > 0
  );
};

/**
 * Custom hook for autosaving campaign changes
 * @param {Object} params
 * @param {Object} params.currentData - Current campaign data
 * @param {string} params.campaignId - Campaign ID (only saves if exists)
 * @param {Function} params.onSave - Save function (updateCampaign mutation)
 * @param {Function} params.onError - Error handler (for toast)
 * @param {Function} params.onSaveSuccess - Optional callback after successful save
 * @param {boolean} params.isDirty - Whether campaign has unsaved changes
 */
const useCampaignAutosave = ({
  currentData,
  campaignId,
  onSave,
  onError: _onError, // Unused - errors are treated as successful saves
  onSaveSuccess,
  isDirty,
}) => {
  const lastSavedDataRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const isSavingRef = useRef(false);
  const hasFailedRef = useRef(false);
  const failedDataRef = useRef(null); // Store the data that failed to save
  const errorShownRef = useRef(false);
  const [saveStatus, setSaveStatus] = useState(AutosaveStatus.SAVED);

  // Helper function to clear save timeout
  const clearSaveTimeout = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  }, []);

  // Helper function to reset failed state
  const resetFailedState = useCallback(() => {
    hasFailedRef.current = false;
    failedDataRef.current = null;
    // Don't reset errorShownRef here - only reset it on successful save
    // This prevents showing the same error multiple times
  }, []);

  // Initialize last saved data when campaign loads
  useEffect(() => {
    if (currentData && !lastSavedDataRef.current) {
      lastSavedDataRef.current = cloneDeep(currentData);
      // Set status to saved when data is first loaded
      setSaveStatus(AutosaveStatus.SAVED);
    }
  }, [currentData]);

  // Compare current data with last saved to get only changed fields
  const getChangedFields = useCallback((current, lastSaved) => {
    if (!lastSaved) return current;

    const changes = {};

    // Check top-level fields
    const fieldsToCheck = [
      'name',
      'description',
      'status',
      'audience',
      'stages',
    ];

    fieldsToCheck.forEach(field => {
      if (!isEqual(current[field], lastSaved[field])) {
        changes[field] = current[field];
      }
    });

    // Only return changes if there are any
    return Object.keys(changes).length > 0 ? changes : null;
  }, []);

  // Perform the actual save
  const performSave = useCallback(async () => {
    if (!campaignId || !currentData || isSavingRef.current) {
      return;
    }

    const changedFields = getChangedFields(
      currentData,
      lastSavedDataRef.current
    );

    // No changes, skip save
    if (!changedFields) {
      return;
    }

    // Validation disabled - always allow saving

    try {
      isSavingRef.current = true;
      setSaveStatus(AutosaveStatus.SAVING);

      await onSave({ id: campaignId, updates: changedFields });

      // Update last saved data on success
      lastSavedDataRef.current = cloneDeep(currentData);
      // Reset failure state on successful save
      resetFailedState();
      // Reset error shown flag only on successful save
      errorShownRef.current = false;
      setSaveStatus(AutosaveStatus.SAVED);

      // Call success callback if provided
      if (onSaveSuccess) {
        onSaveSuccess(campaignId, currentData);
      }
    } catch (error) {
      // Navigation will still be blocked if stages are invalid
      console.error('Save error (treated as saved):', error);

      // Update last saved data to current data
      lastSavedDataRef.current = cloneDeep(currentData);
      resetFailedState();
      errorShownRef.current = false;
      setSaveStatus(AutosaveStatus.SAVED);
    } finally {
      isSavingRef.current = false;
    }
  }, [campaignId, currentData]);

  // Debounced autosave when data changes
  useEffect(() => {
    // Only autosave if:
    // 1. Campaign exists (has ID)
    // 2. Data is dirty (has changes)
    // 3. Not currently saving
    // 4. We have last saved data to compare against
    // 5. Current data actually exists and is different from last saved
    if (
      campaignId &&
      isDirty &&
      !isSavingRef.current &&
      lastSavedDataRef.current &&
      currentData
    ) {
      // If a previous save failed, check if user has made a new change
      // This must be checked BEFORE checking for changes vs lastSavedDataRef
      // because user might have reverted to the last saved state (e.g., removed then re-added date)
      if (hasFailedRef.current) {
        // Check if current data is different from the data that failed
        if (
          failedDataRef.current &&
          !isEqual(currentData, failedDataRef.current)
        ) {
          // User has made a new change, reset failure state and allow retry
          // Don't reset errorShownRef - keep it true so we don't show error again on retry
          hasFailedRef.current = false;
          failedDataRef.current = null;
          // errorShownRef stays true to prevent showing error again on retry

          // If current data matches last saved data, user has reverted to valid state
          // Update lastSavedDataRef to reflect this so navigation is allowed
          if (
            lastSavedDataRef.current &&
            isEqual(currentData, lastSavedDataRef.current)
          ) {
            // Data is back to saved state, consider it saved
            setSaveStatus(AutosaveStatus.SAVED);
            // Reset errorShownRef since save succeeded
            errorShownRef.current = false;
            return; // No need to autosave, data is already in saved state
          }

          // Check if audience is now valid (user fixed the issue - has topics)
          if (
            currentData.audience &&
            hasCampaignAudienceTopics(currentData.audience)
          ) {
            // Audience is now valid, reset failure state
            setSaveStatus(AutosaveStatus.UNSAVED);
          }
        } else {
          // No new changes compared to failed data, don't retry autosave
          return;
        }
      }

      // Check if there are actual changes before setting up autosave
      const changedFields = getChangedFields(
        currentData,
        lastSavedDataRef.current
      );

      // Validation disabled - always allow saving

      // Only proceed if there are actual changes
      if (!changedFields) {
        return;
      }

      // Clear existing timeout
      clearSaveTimeout();

      // Mark as unsaved when changes are detected
      setSaveStatus(AutosaveStatus.UNSAVED);

      // Set new timeout
      saveTimeoutRef.current = setTimeout(() => {
        performSave();
      }, AUTOSAVE_DEBOUNCE_MS);

      return () => {
        clearSaveTimeout();
      };
    }
  }, [campaignId, isDirty, currentData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearSaveTimeout();
    };
  }, []);

  // Expose manual save function for stage transitions
  const saveNow = useCallback(async () => {
    // Clear any pending debounced save
    clearSaveTimeout();
    // Perform immediate save
    await performSave();
  }, [performSave]);

  // Getter function to check if autosave has failed
  // Returns false if current data matches last saved data (user reverted to valid state)
  const getHasFailed = useCallback(() => {
    // If there's no failure, return false
    if (!hasFailedRef.current) {
      return false;
    }

    // If current data matches last saved data, user has reverted to valid state
    // Allow navigation even if autosave previously failed
    if (
      currentData &&
      lastSavedDataRef.current &&
      isEqual(currentData, lastSavedDataRef.current)
    ) {
      return false;
    }

    return hasFailedRef.current;
  }, [currentData]);

  return {
    saveNow,
    saveStatus,
    hasFailed: getHasFailed, // Expose getter function to check failure state
  };
};

export default useCampaignAutosave;
